<template>
  <custom-callout
    ref="detail-ref"
    :border-color-hex="value.statusColor"
    class-main="mt-3 rounded"
    :controlled="true"
    :opened="value.isOpen"
    @click:open="$emit('on-open-close')"
  >
    <div class="row mr-md-0 align-items-center">
      <div class="col-12 col-md-3">
        <div class="d-flex flex-column color-black">
          <div
            class="mb-1 font-light badge badge-pill font-16 py-2 mt-2 w-90 mx-auto text-wrap"
            :style="statusColorStyle"
          >
            {{ value.status | empty }}
          </div>
        </div>
        <div class="col-12">
          <span class="data-title d-block border-mobile">
            {{ value.requestType }}
          </span>
        </div>
        <div class="col-12">
          <strong>{{ value.requestNumber }}</strong>
        </div>
      </div>
      <div class="col-12 col-md-9 border-left py-2">
        <div class="row">
          <div class="col-4">
            <span class="data-title d-block border-mobile">Estudiante:</span>
            <span class="font-16 color-black">
              {{ value.studentName | empty }} - {{ value.studentSie | empty }}
            </span>
          </div>
          <div class="col-2 my-2">
            <span class="data-title d-block border-mobile">
              Fecha de Solicitud:
            </span>
            <span class="font-16 color-black">
              {{ value.requestedDate | date('DD/MMM/YYYY') }}
            </span>
          </div>
          <div class="col-4 my-2">
            <span class="data-title d-block border-mobile">Disciplina:</span>
            <span class="font-16 color-black">
              {{ value.discipline | empty }}
            </span>
          </div>
          <div class="col-2 my-2">
            <drop-down :items="actionItems" />
          </div>
        </div>
        <div class="row">
          <div class="col-3 my-2">
            <span class="data-title d-block border-mobile">
              Proveedor Solicitante:
            </span>
            <span class="font-16 color-black">
              {{ value.provider | empty }}
            </span>
          </div>
          <div class="col-3 my-2">
            <span class="data-title d-block border-mobile">Especialista:</span>
            <span class="font-16 color-black">
              {{ value.specialist | empty }}
            </span>
          </div>
          <div class="col-2 my-2">
            <div v-if="value.dueInterventions">
              <validation-observer ref="interventionForm" novalidate tag="form">
                <span
                  v-if="!editingInterventions"
                  class="data-title d-block border-mobile"
                >
                  <span class="data-title d-block border-mobile">
                    Intervenciones Adeudadas:
                  </span>
                  <span class="text-danger font-weight-bold font-style-normal">
                    {{ displayedDueInterventions }}
                  </span>
                </span>
                <validation-provider
                  v-else
                  v-slot="{ errors }"
                  rules="required"
                >
                  <div class="row">
                    <div class="col-12">
                      <custom-input
                        v-model.number="editedInterventionValue"
                        custom-error-msg="La cantidad de intervenciones adeudadas es requeridas."
                        :error="errors[0]"
                        type="number"
                      />
                    </div>
                    <div class="col-12">
                      <action-button
                        class="float-right"
                        i="fas fa-times-circle"
                        variant="outline-danger mb-4 mt-3"
                        @click="toggleEditInterventions"
                      />
                      <action-button
                        class="float-right mr-2"
                        i="fas fa-save fa-lg"
                        variant="success mt-3"
                        @click="saveInterventions"
                      />
                    </div>
                  </div>
                </validation-provider>
              </validation-observer>
            </div>
          </div>
          <div class="col-2 my-2">
            <span class="data-title d-block border-mobile">
              <span class="data-title d-block border-mobile">
                Intervenciones Ofrecidas:
              </span>
              <span class="text-green font-weight-bold font-style-normal">
                {{ displayedOfferedInterventions }}
              </span>
            </span>
          </div>
          <div class="col-2 my-2">
            <span class="data-title d-block border-mobile">
              <span class="data-title d-block border-mobile">
                Balance Actual:
              </span>
              <span class="text-black font-weight-bold font-style-normal">
                {{ displayedCurrentBalance }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
    <template #edition>
      <div class="p-2">
        <div class="bg-white rounded p-md-3 px-md-4">
          <custom-tab
            v-if="
              value.viewToDisplay === viewDisplayEnum.DOCUMENTS ||
                value.viewToDisplay === viewDisplayEnum.HISTORY_REQUEST
            "
          >
            <custom-tab-item
              :active="value.viewToDisplay === viewDisplayEnum.DOCUMENTS"
              lazy
              title="Documentos"
              @click="() => $emit('on-open-documents')"
            >
              <div class="ml-2 ml-md-5 ml-sm-2 mr-4 pb-3">
                <compensatory-service-documents
                  :academic-year-id="value.academicYearId"
                  :request-id="value.requestId"
                  :request-status-id="value.statusId"
                />
              </div>
            </custom-tab-item>
            <custom-tab-item
              :active="value.viewToDisplay === viewDisplayEnum.HISTORY_REQUEST"
              lazy
              title="Historial de Estatus"
              @click="() => $emit('on-open-request-histories')"
            >
              <div class="ml-2 ml-md-5 ml-sm-2 mr-4 pb-3">
                <div class="row mt-3">
                  <div class="col col-sm col-12 pt-2">
                    <em class="fas fa-clock text-orange mr-1" />
                    <span class="color-gray-7 font-16 font-weight-bold">
                      Historial de Estatus
                    </span>
                  </div>
                </div>
                <div class="py-3 mr-md-0">
                  <history-request-status-log :status="value.statusLogs" />
                </div>
              </div>
            </custom-tab-item>
          </custom-tab>
          <div v-if="value.viewToDisplay === viewDisplayEnum.SUBMIT">
            <accept-request
              :submit-status-id="requestStatus.Sometido"
              :title="'Razón Aceptación'"
              :value="value"
              @on-cancel="$emit('on-open-close')"
              @on-save="$emit('refresh')"
            />
          </div>
          <div
            v-if="
              value.viewToDisplay === viewDisplayEnum.RETURN_TO_PREVIOUS_STATUS
            "
          >
            <request-return-to-previous-status
              :data="value"
              @cancel="$emit('on-open-close')"
              @save="$emit('refresh')"
            />
          </div>
          <!-- <div v-if="value.viewToDisplay === viewDisplayEnum.PENDING_APROVAL">
            <reject-request
              :submit-status-id="requestStatus.CompletadoPendientedeAprobacion"
              :title="'Razón de Denegación'"
              :value="value"
              @on-cancel="$emit('on-open-close')"
              @on-save="$emit('refresh')"
            />
          </div> -->
          <div v-if="value.viewToDisplay === viewDisplayEnum.ACCEPT">
            <accept-request
              :submit-status-id="requestStatus.Aprobado"
              :title="'Razón de Aprobación'"
              :value="value"
              @on-cancel="$emit('on-open-close')"
              @on-save="$emit('refresh')"
            />
          </div>
          <div v-if="value.viewToDisplay === viewDisplayEnum.DENY">
            <deny-request
              title="Determinación de Análisis de Cierre"
              :value="value"
              @on-cancel="$emit('on-open-close')"
              @on-save="$emit('refresh')"
            />
          </div>
          <div v-if="value.viewToDisplay === viewDisplayEnum.REJECT">
            <reject-request
              title="Rechazo de Solicitudes Compensatorias"
              :value="value"
              @on-cancel="$emit('on-open-close')"
              @on-save="$emit('refresh')"
            />
          </div>
          <div
            v-if="
              value.viewToDisplay ===
                viewDisplayEnum.APPROVE_COMPLETE_PENDING_APPROVAL
            "
          >
            <pending-approval-request
              :value="value"
              @on-cancel="$emit('on-open-close')"
              @on-save="$emit('refresh')"
            />
          </div>
        </div>
      </div>
    </template>
  </custom-callout>
</template>

<script>
import CustomCallout from 'custom-callout';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import DropDown from 'dropdown';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import viewDisplay from '@/utils/constants/compensatoryRequestViewDisplay';
import AcceptRequest from './AcceptRequest.vue';
import DenyRequest from './DenyRequest.vue';
import RejectRequest from './RejectRequest.vue';
import PendingApprovalRequest from './PendingApprovalRequest.vue';
import compensatoryRequestStatus from '@/utils/constants/compensatoryRequestStatus';
import CompensatoryServiceDocuments from '@/views/uspqrp/contract-management/components/tabs/CompensatoryServiceDocuments.vue';
import HistoryRequestStatusLog from './HistoryRequestStatusLog.vue';
import RequestReturnToPreviousStatus from './RequestReturnToPreviousStatus.vue';
import { editInterventions } from '@/services/provider/compensatoryRequest.api';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

export default {
  name: 'ResultRow',
  mixins: [alertMixin],
  components: {
    CustomCallout,
    CustomTab,
    CustomTabItem,
    DropDown,
    CustomInput,
    ActionButton,
    AcceptRequest,
    DenyRequest,
    RejectRequest,
    CompensatoryServiceDocuments,
    HistoryRequestStatusLog,
    RequestReturnToPreviousStatus,
    PendingApprovalRequest,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    documents: [],
    editingInterventions: false,
    editedInterventionValue: null,
  }),
  computed: {
    displayedDueInterventions() {
      if (
        this.value.statusId === compensatoryRequestStatus.SolicitudRechazada ||
        this.value.statusId === compensatoryRequestStatus.SolicitudCompletada
      ) {
        return 0;
      }
      return this.value.dueInterventions;
    },
    displayedOfferedInterventions() {
      if (
        this.value.statusId === compensatoryRequestStatus.SolicitudRechazada ||
        this.value.statusId === compensatoryRequestStatus.SolicitudCompletada
      ) {
        return 0;
      }
      return this.value.offeredInterventions;
    },
    displayedCurrentBalance() {
      if (
        this.value.statusId === compensatoryRequestStatus.SolicitudRechazada ||
        this.value.statusId === compensatoryRequestStatus.SolicitudCompletada
      ) {
        return 0;
      }
      return this.value.currentBalance;
    },
    requestStatus() {
      return compensatoryRequestStatus;
    },
    viewDisplayEnum() {
      return viewDisplay;
    },
    statusColorStyle() {
      if (this.value.statusColor) {
        return `background-color: ${this.value.statusColor}`;
      }
      return '';
    },
    canViewAcceptCancel() {
      return (
        (this.value.statusId == compensatoryRequestStatus.Sometido &&
          this.userHasPermissions(
            'rempro.provider.compensatoryrequest.determinate'
          )) ||
        (this.value.statusId ==
          compensatoryRequestStatus.CompletadoPendientedeAprobacion &&
          this.userHasPermissions(
            'rempro.provider.compensatoryrequest.completedpendingapproval'
          ))
      );
    },
    canDeny() {
      return (
        this.denyRequestApply.includes(this.value.statusId) &&
        this.userHasPermissions(
          'rempro.provider.compensatoryrequest.completedpendingapproval'
        )
      );
    },
    canReject() {
      return this.rejectRequestApply.includes(this.value.statusId);
    },
    actionItems() {
      return [
        {
          label: 'Ver Solicitud Servicios Compensatorios',
          icon: 'fas fa-calendar-times color-green',
          click: () =>
            this.$router.push({
              name: 'compensatory-service',
              params: {
                requestId: this.value.requestId,
                academicYearId: this.value.academicYearId,
              },
            }),
        },
        {
          label: 'Ver Documentos',
          icon: 'fas fa-file-search color-green',
          click: () => {
            this.$emit('on-open-documents');
          },
          visible: true,
        },
        {
          label: 'Análisis de Cierre',
          icon: 'fas fa-clipboard-list-check color-green',
          click: () => {
            this.$router.push({
              name: 'servicesClosingAnalysis',
              params: {
                requestId: this.value.requestId,
              },
            });
          },
          visible: this.analysisApply.includes(this.value.statusId),
        },
        {
          label: 'Editar intervenciones adeudadas',
          icon: 'fas fa-file-edit color-green',
          click: this.toggleEditInterventions,
          visible: this.userHasPermissions(
            'rempro.provider.compensatoryrequest.editinterventionsdue'
          ),
        },
        {
          label: 'Historial de Solicitud',
          icon: 'far fa-history color-green',
          click: () => this.$emit('on-open-request-histories'),
          visible: true,
        },
        {
          label: 'Regresar Estatus',
          icon: 'fas fa-undo color-green',
          border: 'top',
          visible:
            this.returnStatusApply.includes(this.value.statusId) &&
            this.userHasPermissions(
              'rempro.provider.compensatoryrequest.returnstatus'
            ),
          click: () => {
            this.$emit('on-open-return-to-previous-status', false);
          },
        },
        {
          label: 'Aceptar',
          icon: 'fas fa-check-square color-green',
          visible:
            this.value.statusId ==
              compensatoryRequestStatus.EnProcesoDiscusion ||
            this.value.statusId == compensatoryRequestStatus.DevueltoAProveedor,
          click: () => this.$emit('on-show-submit'),
        },
        {
          label: 'Aprobar',
          icon: 'fas fa-check-square color-green',
          visible: this.canViewAcceptCancel,
          click: () => this.$emit('on-show-approved'),
        },
        {
          label: 'Denegar',
          icon: 'fas fa-times-square color-red',
          visible: this.canDeny,
          click: () => this.$emit('on-show-deny'),
        },
        {
          label: 'Rechazar',
          icon: 'fas fa-times-square color-red',
          visible: this.canReject,
          click: () => this.$emit('on-show-reject'),
        },
      ];
    },
    analysisApply() {
      return [
        this.requestStatus.Aprobado,
        this.requestStatus.DevueltoAProveedor,
        this.requestStatus.CompletadoPendientedeAprobacion,
        this.requestStatus.ExcluidoPorEstrategia,
      ];
    },
    returnStatusApply() {
      return [
        this.requestStatus.DevueltoAProveedor,
        this.requestStatus.SolicitudRechazada,
        this.requestStatus.Aprobado,
      ];
    },
    denyRequestApply() {
      return [
        this.requestStatus.Sometido,
        this.requestStatus.CompletadoPendientedeAprobacion,
      ];
    },
    rejectRequestApply() {
      return [
        this.requestStatus.EnProcesoDiscusion,
        this.requestStatus.DevueltoAProveedor,
      ];
    },
  },
  methods: {
    toggleEditInterventions() {
      this.editingInterventions = !this.editingInterventions;
    },
    async saveInterventions() {
      if (this.editedInterventionValue > this.value.dueInterventions) {
        this.ShowToast(
          'Alert',
          'Las intervenciones no pueden ser mayor al balance actual.',
          'error'
        );
        return;
      }

      const payload = {
        compensatoryRequestId: this.value.requestId,
        InterventionAmount: this.editedInterventionValue,
      };

      await editInterventions(payload)
        .then(() => {
          this.editedInterventionValue = null;
          this.toggleEditInterventions();
          this.ShowToast(
            'Proceso Exitoso.',
            'Las intervenciones fueron actualizadas exitosamente.',
            'success'
          );
          this.$emit('refresh');
        })
        .catch((err) =>
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          )
        );
    },
  },
  mounted() {
    this.editedInterventionValue = this.value.dueInterventions;
  },
};
</script>
